import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Check, Sparkles, BadgeCheck, Tag } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  {
    id: "free", name: "Free", price: "$0", per: "forever",
    desc: "Get listed. Get found. No credit card.",
    features: ["1 image per listing", "Contact details", "Standard placement", "Basic stats"],
    cta: "Current plan", disabled: true,
  },
  {
    id: "pro", name: "Pro", price: "$29", per: "/mo",
    desc: "Verified badge + gallery for growing teams.",
    features: ["Verified badge", "Up to 12 images", "Project links", "Document uploads", "Engagement analytics"],
    cta: "Upgrade to Pro", highlight: true,
  },
  {
    id: "premium", name: "Premium", price: "$79", per: "/mo",
    desc: "Top-of-mind placement + ad credits included.",
    features: ["Everything in Pro", "500 ad credits / month", "Priority support", "Branded invoices", "Featured carousel slot"],
    cta: "Go Premium",
  },
];

export default function Pricing() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [validating, setValidating] = useState(false);

  const validateCoupon = async (plan) => {
    if (!coupon.trim()) { setCouponInfo(null); return null; }
    setValidating(true);
    try {
      const amount = plan === "pro" ? 2900 : 7900;
      const { data } = await api.get(`/coupons/${coupon.trim().toUpperCase()}/validate`, { params: { type: "membership", amount_cents: amount } });
      setCouponInfo(data);
      return data;
    } catch (e) {
      setCouponInfo(null);
      toast.error(e.response?.data?.detail || "Invalid coupon");
      return null;
    } finally { setValidating(false); }
  };

  const upgrade = async (plan) => {
    if (user === false) { nav("/login", { state: { from: "/pricing" } }); return; }
    if (!user) return;
    if (!user.email_verified) {
      toast.error("Please verify your email before upgrading.");
      nav("/verify-email");
      return;
    }
    try {
      const { data } = await api.post("/checkout", {
        type: "membership", plan,
        origin_url: window.location.origin,
        coupon_code: coupon.trim() ? coupon.trim().toUpperCase() : null,
      });
      if (data?.url) {
        if (data.free) toast.success("Coupon applied — upgrade is on the house!");
        window.location.href = data.url;
      } else {
        toast.error("Checkout could not be started.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upgrade failed");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-8 py-16" data-testid="pricing-page">
      <div className="text-center mb-14">
        <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-2">/ Pricing</div>
        <h1 className="font-display text-5xl tracking-tight">Simple, scale-as-you-grow.</h1>
        <p className="mt-3 text-zinc-600 max-w-xl mx-auto">Start free. Upgrade for the verified badge, image galleries, and promoted placement.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {PLANS.map((p) => {
          const isCurrent = user && user.membership === p.id;
          return (
            <div key={p.id} data-testid={`pricing-card-${p.id}`} className={`relative rounded-2xl border ${p.highlight ? "border-zinc-900 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.3)]" : "border-zinc-200"} bg-white p-8 flex flex-col`}>
              {p.highlight && <Badge className="absolute -top-3 left-6 surface-navy">Most popular</Badge>}
              <div className="font-display text-2xl">{p.name}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <div className="font-display text-5xl tracking-tight">{p.price}</div>
                <div className="text-sm text-zinc-500">{p.per}</div>
              </div>
              <p className="mt-3 text-sm text-zinc-600">{p.desc}</p>
              <ul className="mt-6 space-y-2 text-sm text-zinc-700 flex-1">
                {p.features.map((f) => <li key={f} className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 text-emerald-600" />{f}</li>)}
              </ul>
              <div className="mt-8">
                {p.id === "free" ? (
                  <Button asChild variant="outline" className="w-full" data-testid={`pricing-cta-${p.id}`}>
                    <Link to={user ? "/list-business" : "/register"}>{user ? "Create a free listing" : "Sign up free"}</Link>
                  </Button>
                ) : isCurrent ? (
                  <Button disabled className="w-full" data-testid={`pricing-cta-${p.id}`}><BadgeCheck className="h-4 w-4 mr-1" />Current plan</Button>
                ) : (
                  <Button onClick={() => upgrade(p.id)} className={`w-full ${p.highlight ? "btn-brand" : ""}`} data-testid={`pricing-cta-${p.id}`}>
                    {p.cta} <Sparkles className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-14 max-w-md mx-auto">
        <div className="text-xs font-mono-tag uppercase tracking-[0.18em] text-zinc-400 text-center mb-2">Have a coupon?</div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Tag className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="ENTER CODE" className="pl-9 uppercase font-mono-tag" data-testid="pricing-coupon-input" />
          </div>
          <Button variant="outline" onClick={() => validateCoupon("pro")} disabled={!coupon.trim() || validating} data-testid="pricing-coupon-apply">{validating ? "…" : "Apply"}</Button>
        </div>
        {couponInfo && (
          <div className="mt-3 text-sm text-emerald-700 text-center" data-testid="pricing-coupon-applied">
            ✓ {couponInfo.code} — {couponInfo.discount_type === "percent" ? `${couponInfo.value}% off` : `$${(couponInfo.value/100).toFixed(2)} off`}
            {couponInfo.free && <span className="ml-1 font-semibold">(plan is free!)</span>}
          </div>
        )}
      </div>

      <div className="mt-10 text-center text-xs text-zinc-500 font-mono-tag">
        Payments are processed securely via PayPal. Cancel anytime.
      </div>
    </div>
  );
}
